/**
 * Hand bytes to the browser as a download. ONE implementation.
 *
 * There are two distinct download jobs in this app and conflating them is what the
 * 2026-08-09 export audit untangled:
 *
 *   1. the page BUILDS a table from rows it holds  → `utils/exportTable.ts`
 *      (CSV/Excel/PDF, escaping, the UTF-8 BOM Excel needs)
 *   2. the BACKEND produced the bytes and the page only delivers them  → this file
 *
 * Job 2 looks trivial and is not. It was written four times — `api/organization.ts`'s
 * private `downloadCsv`, plus WorkerDocuments, SystemBilling and WorkerProfile — and
 * three of the four revoked the object URL SYNCHRONOUSLY. `downloadCsv` had already
 * learned that this races the download in Safari and left a comment saying so; the
 * copies did not have the comment, so they did not have the fix. That is
 * `svdy-rules` §10.5 in one paragraph.
 *
 * Why an object URL at all rather than a plain `<a href>`: every one of these endpoints
 * is authenticated and org-scoped, and an anchor sends neither the bearer token nor
 * `X-Organization-Id`. The bytes have to come through the API client.
 */

/**
 * Trigger a download of `blob` as `filename`.
 *
 * The URL is revoked on the NEXT TICK, deliberately. Revoking synchronously can cancel
 * the download in Safari before it starts; never revoking keeps the whole blob alive
 * for the rest of the session.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Same, for text the backend returned as a string rather than a Blob.
 *
 * `type` defaults to CSV because that is every current caller. Note this helper does
 * NOT add a BOM: the backend writes `utf-8-sig` itself for the files it generates
 * (the importer's templates and the documents manifest both do), and adding a second
 * BOM would put a stray `ï»¿` in the first cell.
 */
export function downloadText(
  text: string,
  filename: string,
  type = 'text/csv;charset=utf-8',
): void {
  downloadBlob(new Blob([text], { type }), filename);
}
